import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import { Box, Card, Chip, Link, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { api, unwrap, type Schemas } from '../../api/client';
import { DataTable, type Column } from '../../components/DataTable';
import { Money } from '../../components/Money';
import { EmptyState } from '../../components/states';
import { businessDate, dateTime, money } from '../../lib/format';
import { useShop } from '../../shop/ShopProvider';
import { contactKeys, documentHref, type Contact, type Ledger, type PartyBalance } from './model';
import { BalanceText } from './ui';

type Entry = Schemas['PartyLedgerEntry'];

export function DocumentCell({ doc }: { doc: Entry['document'] }) {
  const href = documentHref(doc.type, doc.id);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
        {doc.label}
      </Typography>
      {doc.number && (
        <Typography variant="caption" component="div" color="text.secondary" noWrap>
          {href ? (
            <Link
              component={RouterLink}
              to={href}
              underline="hover"
              onClick={(e) => e.stopPropagation()}
            >
              {doc.number}
            </Link>
          ) : (
            doc.number
          )}
        </Typography>
      )}
    </Box>
  );
}

function Figure({
  label,
  children,
  loading,
}: {
  label: string;
  children: ReactNode;
  loading: boolean;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 140, px: { xs: 2, sm: 2.5 }, py: 2 }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      {loading ? <Skeleton width="70%" height={28} /> : children}
    </Box>
  );
}

function BalanceFigure({ balance }: { balance: PartyBalance }) {
  return <BalanceText balance={balance} align="left" />;
}

/** The party ledger: opening, entries with a running balance, closing. */
export function LedgerTab({
  contact,
  ledger,
  from,
  to,
}: {
  contact: Contact;
  ledger: Ledger;
  from: string;
  to: string;
}) {
  const { format } = useShop();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [prevKey, setPrevKey] = useState(`${ledger}${from}${to}`);
  if (prevKey !== `${ledger}${from}${to}`) {
    setPrevKey(`${ledger}${from}${to}`);
    setPage(1);
  }
  const query = { ledger, from: from || undefined, to: to || undefined, page, pageSize };
  const data = useQuery({
    queryKey: contactKeys.ledger(contact.id, query),
    queryFn: () =>
      unwrap(api.GET('/contacts/{id}/ledger', { params: { path: { id: contact.id }, query } })),
    placeholderData: keepPreviousData,
  });
  const d = data.data;

  const columns: Column<Entry>[] = [
    {
      key: 'date',
      header: 'Date',
      width: 120,
      render: (e) => (
        <Tooltip
          title={`Posted ${dateTime(e.postedAt, format)}${e.postedBy ? ` by ${e.postedBy.name}` : ''}`}
        >
          <span style={{ whiteSpace: 'nowrap' }}>{businessDate(e.entryDate, format)}</span>
        </Tooltip>
      ),
    },
    {
      key: 'doc',
      header: 'Document',
      render: (e) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <DocumentCell doc={e.document} />
          {e.status !== 'POSTED' && (
            <Chip
              size="small"
              variant="outlined"
              color={e.status === 'REVERSED' ? 'error' : 'default'}
              label={e.status === 'REVERSED' ? 'Reversed' : 'Reversal'}
              sx={{ height: 20, fontSize: 11 }}
            />
          )}
        </Stack>
      ),
    },
    {
      key: 'desc',
      header: 'Details',
      hideBelow: 'lg',
      render: (e) => (
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          sx={{ maxWidth: 280 }}
          title={e.description ?? ''}
        >
          {e.document.reference ?? e.description ?? '—'}
        </Typography>
      ),
    },
    {
      key: 'debit',
      header: 'Debit',
      align: 'right',
      hideBelow: 'sm',
      render: (e) => (Number(e.debit) ? <Money value={e.debit} /> : <Dash />),
    },
    {
      key: 'credit',
      header: 'Credit',
      align: 'right',
      hideBelow: 'sm',
      render: (e) => (Number(e.credit) ? <Money value={e.credit} /> : <Dash />),
    },
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      render: (e) => <BalanceText balance={e.balance} dense />,
    },
  ];

  return (
    <Stack spacing={2}>
      <Card>
        <Stack
          direction="row"
          sx={{
            flexWrap: 'wrap',
            '& > *:not(:last-child)': { borderRight: { sm: 1 }, borderColor: { sm: 'divider' } },
          }}
        >
          <Figure label={from ? `Opening · ${businessDate(from, format)}` : 'Opening'} loading={!d}>
            {d && <BalanceFigure balance={d.openingBalance} />}
          </Figure>
          <Figure label="Debits" loading={!d}>
            <Typography variant="body2" className="tabular" sx={{ fontWeight: 650 }}>
              {d && money(d.totals.debit, format)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {d && `${String(d.totals.entries)} ${d.totals.entries === 1 ? 'entry' : 'entries'}`}
            </Typography>
          </Figure>
          <Figure label="Credits" loading={!d}>
            <Typography variant="body2" className="tabular" sx={{ fontWeight: 650 }}>
              {d && money(d.totals.credit, format)}
            </Typography>
          </Figure>
          <Figure label={`Closing · ${d ? businessDate(d.period.to, format) : ''}`} loading={!d}>
            {d && <BalanceFigure balance={d.closingBalance} />}
          </Figure>
        </Stack>
      </Card>
      <DataTable
        columns={columns}
        rows={d?.data}
        rowKey={(e) => e.lineId}
        loading={data.isLoading}
        page={d?.page}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(1);
        }}
        empty={
          <EmptyState
            icon={<ReceiptLongRounded />}
            title="No entries in this period"
            body={
              from
                ? 'Nothing was posted for this contact between these dates. Try a wider period.'
                : 'Invoices, bills, payments and opening balances will appear here once posted.'
            }
          />
        }
      />
    </Stack>
  );
}

function Dash() {
  return (
    <Typography component="span" variant="body2" color="text.disabled">
      —
    </Typography>
  );
}
